import { useEffect } from "react";
import { Controller, useForm } from "react-hook-form";
import moment, { Moment } from "moment";
import {
  Autocomplete,
  Box,
  Button,
  Checkbox,
  Chip,
  FormControl,
  Grid,
  InputLabel,
  ListItemText,
  MenuItem,
  Select,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from "@mui/material";
import { FormSelect } from "../../components/FormSelect";
import { FormInput } from "../../components/FormInput";
import { FormDatePicker } from "../../components/FormDatePicker";
import { ISidekickPlant } from "../../types/api/sidekick-usage.type";
import { RANGE_PRESETS, RangePreset, SidekickFilters } from "./urlState";
import {
  ASKER_OPTIONS,
  CLIENT_OPTIONS,
  FLAG_OPTIONS,
  HAS_COMMENT_OPTIONS,
  RATING_OPTIONS,
} from "./labels";

interface FormValues {
  envIds: number[];
  range: RangePreset;
  // Moment values from the date pickers. Typed loosely because react-hook-form's
  // deep path types cannot walk the Moment class.
  from: any;
  to: any;
  users: string[];
  asker: string;
  rating: string[];
  hasComment: string;
  flags: string[];
  client: string;
  model: string;
  q: string;
}

const toFormValues = (f: SidekickFilters): FormValues => ({
  envIds: f.envIds,
  range: f.range,
  from: f.from ? moment(f.from) : moment().subtract(7, "days").startOf("minute"),
  to: f.to ? moment(f.to) : moment().startOf("minute"),
  users: f.users,
  asker: f.asker,
  rating: f.rating,
  hasComment: f.hasComment || "any",
  flags: f.flags,
  client: f.client || "any",
  model: f.model,
  q: f.q,
});

const fromFormValues = (v: FormValues, current: SidekickFilters): SidekickFilters => ({
  envIds: v.envIds,
  range: v.range,
  from: v.range === "custom" && v.from ? moment(v.from).toISOString() : "",
  to: v.range === "custom" && v.to ? moment(v.to).toISOString() : "",
  users: v.users.map((u) => u.trim()).filter(Boolean),
  asker: v.asker as SidekickFilters["asker"],
  rating: v.rating as SidekickFilters["rating"],
  hasComment: (v.hasComment === "any" ? "" : v.hasComment) as SidekickFilters["hasComment"],
  flags: v.flags as SidekickFilters["flags"],
  client: (v.client === "any" ? "" : v.client) as SidekickFilters["client"],
  model: v.model.trim(),
  q: v.q.trim(),
  page: 1, // any filter change starts again from the newest conversations
  pageSize: current.pageSize,
});

/** Multi-choice dropdown with checkboxes, bound to the form. */
const MultiChoice = ({
  control,
  name,
  id,
  label,
  options,
}: {
  control: any;
  name: keyof FormValues;
  id: string;
  label: string;
  options: { value: string; label: string }[];
}) => (
  <Controller
    name={name}
    control={control}
    render={({ field: { value, onChange } }) => (
      <FormControl fullWidth>
        <InputLabel id={`${id}-label`}>{label}</InputLabel>
        <Select
          labelId={`${id}-label`}
          id={id}
          multiple
          value={(value as string[]) ?? []}
          label={label}
          onChange={(e) =>
            onChange(typeof e.target.value === "string" ? e.target.value.split(",") : e.target.value)
          }
          renderValue={(selected) =>
            (selected as string[])
              .map((s) => options.find((o) => o.value === s)?.label ?? s)
              .join(", ")
          }
        >
          {options.map((o) => (
            <MenuItem key={o.value} value={o.value}>
              <Checkbox size="small" checked={((value as string[]) ?? []).includes(o.value)} />
              <ListItemText primary={o.label} />
            </MenuItem>
          ))}
        </Select>
      </FormControl>
    )}
  />
);

interface FiltersPanelProps {
  plants: ISidekickPlant[];
  knownUsers: string[];
  value: SidekickFilters;
  onApply: (filters: SidekickFilters) => void;
  onReset: () => void;
}

const FiltersPanel = ({ plants, knownUsers, value, onApply, onReset }: FiltersPanelProps) => {
  const {
    control,
    register,
    reset,
    handleSubmit,
    setValue,
    watch,
    formState: { errors },
  } = useForm<FormValues>({ mode: "all", defaultValues: toFormValues(value) });

  // Keep the form in step with the URL (back/forward, pasted links).
  useEffect(() => {
    reset(toFormValues(value));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [JSON.stringify(value)]);

  const range = watch("range");
  const selectablePlants = plants.filter((p) => p.status !== "suspended");
  const plantById = new Map(plants.map((p) => [p.env_id, p]));
  const fieldProps = { control: control as any, register: register as any, errors };

  return (
    <Box component="form" onSubmit={handleSubmit((v) => onApply(fromFormValues(v, value)))} noValidate>
      <Grid container spacing={2}>
        <Grid item xs={12} md={8}>
          <Controller
            name="envIds"
            control={control}
            rules={{ validate: (ids) => ids.length > 0 || "Choose at least one plant" }}
            render={({ field: { value: ids, onChange } }) => (
              <Autocomplete
                id="sidekick-plants"
                multiple
                disableCloseOnSelect
                limitTags={4}
                options={plants}
                value={ids.map((id) => plantById.get(id)).filter(Boolean) as ISidekickPlant[]}
                getOptionLabel={(p) => p.name}
                getOptionDisabled={(p) => p.status === "suspended"}
                isOptionEqualToValue={(a, b) => a.env_id === b.env_id}
                onChange={(_, selected) => onChange(selected.map((p) => p.env_id))}
                renderOption={(props, p, { selected }) => (
                  <li {...props} key={p.env_id}>
                    <Checkbox size="small" checked={selected} sx={{ mr: 1 }} />
                    {p.name}
                    {p.status === "suspended" && (
                      <Typography component="span" color="#999" sx={{ ml: 1 }}>
                        (suspended)
                      </Typography>
                    )}
                  </li>
                )}
                renderTags={(selected, getTagProps) =>
                  selected.map((p, index) => (
                    <Chip size="small" label={p.name} {...getTagProps({ index })} key={p.env_id} />
                  ))
                }
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Plants"
                    placeholder="Search plants"
                    error={!!errors.envIds}
                    helperText={(errors.envIds as any)?.message as string | undefined}
                  />
                )}
              />
            )}
          />
        </Grid>
        <Grid item xs={12} md={4} sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <Button
            variant="outlined"
            onClick={() =>
              setValue("envIds", selectablePlants.map((p) => p.env_id), { shouldValidate: true })
            }
          >
            Select all plants
          </Button>
          <Button onClick={() => setValue("envIds", [], { shouldValidate: true })}>Clear</Button>
        </Grid>

        <Grid item xs={12}>
          <Controller
            name="range"
            control={control}
            render={({ field: { value: r, onChange } }) => (
              <ToggleButtonGroup
                exclusive
                size="small"
                color="primary"
                value={r}
                onChange={(_, next) => next && onChange(next)}
                aria-label="Time range"
              >
                {RANGE_PRESETS.map((p) => (
                  <ToggleButton key={p.value} value={p.value}>
                    {p.label}
                  </ToggleButton>
                ))}
              </ToggleButtonGroup>
            )}
          />
        </Grid>
        {range === "custom" && (
          <>
            <Grid item xs={12} sm={6} md={3}>
              <FormDatePicker
                id="sidekick-from"
                name="from"
                label="From"
                rules={{ required: "Choose a start time" }}
                {...fieldProps}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <FormDatePicker
                id="sidekick-to"
                name="to"
                label="To"
                rules={{
                  required: "Choose an end time",
                  validate: (to: Moment | null) => {
                    const from = watch("from");
                    return !from || !to || moment(to).isAfter(moment(from)) || "End must be after start";
                  },
                }}
                {...fieldProps}
              />
            </Grid>
          </>
        )}

        <Grid item xs={12} md={6}>
          <Controller
            name="users"
            control={control}
            render={({ field: { value: users, onChange } }) => (
              <Autocomplete
                id="sidekick-users"
                multiple
                freeSolo
                options={knownUsers}
                value={users}
                onChange={(_, next) => onChange(next as string[])}
                renderTags={(selected, getTagProps) =>
                  selected.map((email, index) => (
                    <Chip size="small" label={email} {...getTagProps({ index })} key={email} />
                  ))
                }
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="People"
                    placeholder="Type an email and press Enter"
                  />
                )}
              />
            )}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <FormSelect id="sidekick-asker" name="asker" label="Who asked" options={ASKER_OPTIONS} {...fieldProps} />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <FormSelect id="sidekick-client" name="client" label="Used from" options={CLIENT_OPTIONS} {...fieldProps} />
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <MultiChoice control={control} name="rating" id="sidekick-rating" label="Rating" options={RATING_OPTIONS} />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <FormSelect
            id="sidekick-has-comment"
            name="hasComment"
            label="Comment"
            options={HAS_COMMENT_OPTIONS}
            {...fieldProps}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <MultiChoice control={control} name="flags" id="sidekick-flags" label="Problems (must have all)" options={FLAG_OPTIONS} />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <FormInput id="sidekick-model" name="model" label="AI model" {...fieldProps} />
        </Grid>

        <Grid item xs={12}>
          <FormInput
            id="sidekick-q"
            name="q"
            label="Search text"
            helperText="Looks in questions, answers, comments and technical details. Not case-sensitive."
            {...fieldProps}
          />
        </Grid>

        <Grid item xs={12} sx={{ display: "flex", gap: 1 }}>
          <Button type="submit" variant="contained">
            Show results
          </Button>
          <Button onClick={onReset}>Reset filters</Button>
        </Grid>
      </Grid>
    </Box>
  );
};

export default FiltersPanel;
